import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tabs } from '@shared/ui/tabs';
import { Button } from '@shared/ui/button';
import { statusUtil } from '@shared/utils/statusUtils';
import { PageHeader } from '@shared/components/PageHeader';
import {
  ArrowLeft,
  Copy,
  Pencil,
  Tag,
  Calendar,
  CalendarX,
  Users,
  DollarSign,
  User,
  Clock,
  MoreHorizontal,
} from 'lucide-react';
import Overview from './tabs/overview/Overview';
import VersionHistory from './tabs/VersionHistory';
import { ActivityLogTab } from '@features/bx/BulkBillGeneration/components/Detail/Tabs/ActivityLog';
import { TimelineTab } from '@features/cx/shared/components/tabs/TimelineTab';
import { EditRateModal } from '../EditRateModal';
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { useRateDetail, useCloneRate } from '../../hooks';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';

export const RateDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: rateDetail } = useRateDetail(id);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { mutateAsync: cloneRate ,isPending} = useCloneRate();
  const remoteUtilityId = useRemoteUtilityId();
  const handleBack = () => {
    navigate('/plan-tarrif/plan-tarrif-landing?tab=tariff');
  };

  const handleClone = async () => {
    if (id) {
      await cloneRate(Number(id));
    }
  };

  const statusBadgeClass = statusUtil.getClassName(rateDetail?.status || 'Active');

  const metadataFields = [
    { label: 'Effective Date', value: rateDetail?.effectiveDate || '-' },
    { label: 'End Date', value: rateDetail?.expiryDate || '-' },
    { label: 'Created On', value: rateDetail?.createdDate || '-' },
    { label: 'Created By', value: rateDetail?.createdUserRemoteName || '-' },
    { label: 'Updated On', value: rateDetail?.lastModifiedDate || '-' },
    { label: 'Updated By', value: rateDetail?.lastModifiedUserRemoteName || '-' },
  ];

  // --- Tab Definitions ---
  const tabComponents = {
    overview: {
      label: 'Overview',
      component: <Overview rateDetail={rateDetail} />,
    },
    'version-history': {
      label: 'Version History',
      component: <VersionHistory />,
    },
    'activity-log': {
      label: 'Activity Log',
      component: (
        <TimelineTab
          entityId={id}
          remoteUtilityId={remoteUtilityId}
        />
      ),
    },
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack} className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <PageHeader title={'Rate Details'} subtitle={'Detailed overview of rate.'}></PageHeader>
        </div>
        <></>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" isLoading={isPending} onClick={handleClone}>
            <Copy className="h-3.5 w-3.5" /> Clone
          </Button>
          <Button size="sm" onClick={() => setIsEditModalOpen(true)}>
            <Pencil className="h-3.5 w-3.5" /> Edit Rate
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-blue-500 to-blue-600" />
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            {/* Left: Identity */}
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-gray-100">
                <Tag className="h-6 w-6 text-gray-600" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <span className="font-mono text-sm text-gray-500">{rateDetail?.id || '-'}</span>
                  <span className="text-xs text-gray-400">{rateDetail?.version || '1.0'}</span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    {rateDetail?.status || 'Active'}
                  </span>
                </div>
                <h2 className="text-lg font-semibold text-gray-900">{rateDetail?.rateName || 'Loading...'}</h2>
                <p className="text-sm text-gray-500 mt-1 max-w-lg">{rateDetail?.description || ''}</p>

                {/* Tags */}
                <div className="flex items-center gap-2 mt-3">
                  {[rateDetail?.utilityService, rateDetail?.rateTypeDisplay].filter(Boolean).map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2.5 py-1 text-xs font-medium rounded-full border border-blue-200 bg-blue-50 text-blue-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Metadata Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 text-sm shrink-0">
              {metadataFields.map((field, index) => (
                <ValueLabel key={index} label={field.label} value={field.value} />
              ))}
            </div>
          </div>
        </div>
      </div>  

      {/* Tabs */}
      <Tabs
        tabComponents={tabComponents}
        defaultValue="overview"
        urlMapping={{
          overview: 'overview',
          'version-history': 'versions',
          'activity-log': 'activity-log',
        }}
        tabsListFullWidth={true}
        idPrefix="rate-detail-tab"
        level={0}
        tabsListClassName="grid grid-cols-3"
      />

      {/* Edit Modal */}
      <EditRateModal
        isOpen={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        id={Number(id)}
        currentVersion={rateDetail?.version}
      />
    </div>
  );
};
